import React, { useState, useEffect, useCallback } from "react";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useAuth } from "../../contexts/AuthContext";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useProctoring } from "../../hooks/useProctoring";
import ProctoringBanner from "../../components/Proctoring/ProctoringBanner";
import ProctoringSetupModal from "../../components/Proctoring/ProctoringSetupModal";
import FloatingCameraWidget from "../../components/Proctoring/FloatingCameraWidget";
import VerificationGuard from "../../components/VerificationGuard";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import { checkTestAchievements } from "../../services/gamificationService";

const TakeTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { user } = useAuth();
  const db = getFirestore(firebaseApp);
  const { isVerified } = useVerificationStatus();

  // Check if this test is from an HR-shared link (bypasses verification)
  const isHRSharedTest = searchParams.get("source") === "hr-shared";

  const [testDoc, setTestDoc] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({}); // store index
  const [timeLeft, setTimeLeft] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAlreadyTaken, setHasAlreadyTaken] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showProctoringSetup, setShowProctoringSetup] = useState(false);
  const [proctoringInitialized, setProctoringInitialized] = useState(false);
  const [testStarted, setTestStarted] = useState(false);

  // Strict Mode State (for Skills Validation)
  const [questionTimeLeft, setQuestionTimeLeft] = useState(30);
  const isSkillsValidation = testDoc?.isSkillsValidation;

  // Reset question timer when current question changes
  useEffect(() => {
    if (isSkillsValidation) {
      setQuestionTimeLeft(30);
    }
  }, [currentQuestion, isSkillsValidation]);

  // Question Timer Countdown
  useEffect(() => {
    if (!isSkillsValidation || !testStarted || isSubmitting) return;

    if (questionTimeLeft > 0) {
      const timerId = setTimeout(() => {
        setQuestionTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else {
      // Time's up for this question - Auto Advance
      if (currentQuestion < (testDoc?.testData?.sampleQuestions || testDoc?.questions || []).length - 1) {
        handleNextQuestion();
      } else {
        // Last question timeout - maybe autosubmit or just stay? 
        // Usually autosubmit is safer to prevent getting stuck
        handleSubmitTest();
      }
    }
  }, [questionTimeLeft, isSkillsValidation, testStarted, isSubmitting, currentQuestion, testDoc]);

  // Safely derive questions list for rendering and navigation
  const getQuestions = useCallback(() => {
    const raw = (testDoc?.testData?.sampleQuestions ||
      testDoc?.questions ||
      []);
    if (!Array.isArray(raw)) return [];
    return raw.filter((q) => q && Array.isArray(q.options));
  }, [testDoc]);

  // Ensure currentQuestion stays within bounds of valid questions
  useEffect(() => {
    const qs = getQuestions();
    if (qs.length === 0) return;
    if (currentQuestion >= qs.length) {
      setCurrentQuestion(qs.length - 1);
    } else if (currentQuestion < 0) {
      setCurrentQuestion(0);
    }
  }, [getQuestions, currentQuestion]);

  // Initialize proctoring
  const { proctoringStatus, startProctoring, stopProctoring, clearViolations } =
    useProctoring({
      enableTabSwitchDetection: true,
      enableCopyPasteDetection: true,
      enableFullscreenEnforcement: true,
      enableRightClickDisable: true,
      enableKeyboardShortcuts: true,
      enableCameraMonitoring: true,
      enableMicrophoneMonitoring: false,
      maxViolations: 5,
      onViolation: (violation) => {
        if (testId && user) {
          logViolationToFirebase(violation);
        }
      },
      onMaxViolationsReached: () => {
        alert(
          "Too many proctoring violations detected. The test will be terminated."
        );
        handleTerminateTest();
      },
    });

  /* placeholder structure removed */

  /* mock removed
      {
        id: 1,
        type: 'multiple-choice',
        question: 'What is the correct way to handle side effects in React functional components?',
        options: [
          'Using componentDidMount lifecycle method',
          'Using useEffect hook',
          'Using useState hook',
          'Using class-based components only'
        ],
        difficulty: 'medium'
      },
      {
        id: 2,
        type: 'multiple-choice',
        question: 'Which of the following is the correct syntax for creating a React component?',
        options: [
          'function MyComponent() { return <div>Hello</div>; }',
          'const MyComponent = () => { return <div>Hello</div>; }',
          'class MyComponent extends React.Component { render() { return <div>Hello</div>; } }',
          'All of the above'
        ],
        difficulty: 'easy'
      },
      {
        id: 3,
        type: 'multiple-choice',
        question: 'What is the purpose of React.memo()?',
        options: [
          'To memoize expensive calculations',
          'To prevent unnecessary re-renders of functional components',
          'To manage component state',
          'To handle side effects'
        ],
        difficulty: 'medium'
      },
      {
        id: 4,
        type: 'code-completion',
        question: 'Complete the following React hook to manage a counter:',
        code: `const [count, setCount] = useState(0);

const increment = () => {
  // Complete this function
  _______________
};`,
        options: [
          'setCount(count + 1)',
          'setCount(prev => prev + 1)',
          'count = count + 1',
          'Both A and B are correct'
        ],
        difficulty: 'medium'
      },
      {
        id: 5,
        type: 'multiple-choice',
        question: 'What is the Virtual DOM in React?',
        options: [
          'A copy of the real DOM kept in memory',
          'A JavaScript representation of the real DOM',
          'A faster version of the DOM',
          'All of the above'
        ],
        difficulty: 'easy'
      }
*/

  // Log violations to Firebase
  const logViolationToFirebase = async (violation) => {
    try {
      const testResultId = `${user?.id}_${testId}`;
      const testResultDoc = doc(db, "jobseekerResults", testResultId);
      const testSnapshot = await getDoc(testResultDoc);

      let existingViolations = [];
      if (testSnapshot.exists()) {
        existingViolations = testSnapshot.data().proctoringViolations || [];
      }

      await setDoc(
        testResultDoc,
        {
          proctoringViolations: [
            ...existingViolations,
            {
              type: violation.type,
              timestamp: violation.timestamp,
              details: violation.details,
              userId: user?.id,
              questionNumber: currentQuestion + 1,
            },
          ],
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Failed to log violation:", error);
    }
  };

  // Handle test termination due to violations
  const handleTerminateTest = async () => {
    try {
      const testResultId = `${user?.id}_${testId}`;
      await setDoc(
        doc(db, "jobseekerResults", testResultId),
        {
          jobSeekerId: user?.id,
          jobSeekerName: user?.name,
          jobSeekerEmail: user?.email,
          testId,
          hrId: testDoc?.hrId,
          status: "terminated",
          terminationReason: "Too many proctoring violations",
          score: 0,
          mcqScore: 0,
          totalScore: 0,
          totalQuestions: testDoc?.questions?.length || 0,
          correctAnswers: 0,
          answers,
          terminatedAt: serverTimestamp(),
          jobTitle: testDoc?.jobTitle,
          hasCompletedInterview: false,
          qualifiedForInterview: false,
        },
        { merge: true }
      );

      stopProctoring();
      navigate("/job-seeker/results");
    } catch (error) {
      console.error("Failed to terminate test:", error);
    }
  };

  // Handle proctoring setup completion
  const handleStartProctoring = async () => {
    try {
      // Start proctoring - it will handle fullscreen gracefully
      await startProctoring();

      // Always mark  since startProctoring now handles errors gracefully
      setProctoringInitialized(true);
      setShowProctoringSetup(false);
      setTestStarted(true);

      // Log proctoring start
      const testResultId = `${user?.id}_${testId}`;
      await setDoc(
        doc(db, "jobseekerResults", testResultId),
        {
          proctoringStarted: true,
          proctoringStartedAt: serverTimestamp(),
          testStartedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Try to enter fullscreen again (this is being called from a user gesture/button click)
      try {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (fullscreenError) {
        // Fullscreen is not critical - just log and continue
        console.warn("Fullscreen request failed (continuing without fullscreen):", fullscreenError);
      }
    } catch (error) {
      console.error("Failed to start proctoring:", error);
      // Still allow the test to start even if proctoring had issues
      // The proctoring monitoring will work for what it can
      setProctoringInitialized(true);
      setShowProctoringSetup(false);
      setTestStarted(true);
    }
  };

  // Handle stopping proctoring
  const handleStopProctoring = () => {
    stopProctoring();
    setProctoringInitialized(false);
  };

  // Define handleSubmitTest function first
  const handleSubmitTest = useCallback(async () => {
    console.log("🚀 Starting test submission...");
    setIsSubmitting(true);

    // Don't stop proctoring yet - we might proceed to Round 2

    const questions = getQuestions();

    const getCorrectIndex = (question) => {
      const options = Array.isArray(question?.options)
        ? question.options
        : [];
      const correctAnswer = question?.correctAnswer;

      if (typeof correctAnswer === "number") {
        if (correctAnswer >= 0 && correctAnswer < options.length)
          return correctAnswer; // 0-based
        if (correctAnswer >= 1 && correctAnswer <= options.length)
          return correctAnswer - 1; // 1-based
      }
      if (typeof correctAnswer === "string" && options.length > 0) {
        const target = correctAnswer.trim().toLowerCase();
        const foundIndex = options.findIndex(
          (opt) => String(opt).trim().toLowerCase() === target
        );
        if (foundIndex >= 0) return foundIndex;
      }
      return null;
    };

    const correctCount = questions.reduce(
      (sum, q, idx) => {
        const selectedIndex = answers[idx];
        const correctIndex = getCorrectIndex(q);
        if (typeof selectedIndex !== "number" || correctIndex === null)
          return sum;
        return sum + (selectedIndex === correctIndex ? 1 : 0);
      },
      0
    );

    const totalQuestions = questions.length || 1;
    const scorePercent = Math.round((correctCount / totalQuestions) * 100);

    // Calculate MCQ score out of 25 (capped at 25)
    const mcqScore = Math.min((scorePercent / 100) * 25, 25);

    console.log("📊 Test results calculated:", {
      correctCount,
      totalQuestions,
      scorePercent,
      mcqScore,
    });
    console.log("👤 User data:", {
      userId: user?.id,
      userName: user?.name,
      testId,
    });
    console.log("📝 Answers submitted:", answers);

    if (user) {
      try {
        // Check if this is a demo test
        if (testDoc?.testType === "practice" || testId === "demo") {
          console.log(
            "🎯 Processing demo/practice test - skipping database save"
          );
          // Stop proctoring for demo tests
          stopProctoring();
          // Show results without saving to database
          alert(
            `Demo Test Completed!\n\nYour Score: ${scorePercent}%\nCorrect Answers: ${correctCount}/${totalQuestions}\n\nThis w practice test - results were not saved.`
          );
          navigate("/job-seeker");
          return;
        }

        // Check if this is a skills validation test
        const isSkillsValidation =
          testDoc?.testType === "skills_validation" || testDoc?.resumeBasedTest;
        console.log("🧪 Test type check:", {
          isSkillsValidation,
          testType: testDoc?.testType,
          resumeBasedTest: testDoc?.resumeBasedTest,
        });

        // Save initial MCQ results
        console.log("💾 Saving to database...");

        // Calculate correct answer indices for each question
        const correctAnswerIndices = questions.map((q) => getCorrectIndex(q));

        await setDoc(doc(db, "jobseekerResults", `${user.id}_${testId}`), {
          jobSeekerId: user.id,
          jobSeekerName: user.name,
          jobSeekerEmail: user.email,
          testId,
          hrId: testDoc?.hrId || null, // Ensure not undefined
          score: scorePercent, // Original percentage
          mcqScore, // Score out of 25
          totalQuestions: questions.length,
          correctAnswers: correctCount,
          answers,
          questions: questions, // Store full question data for course generation
          correctAnswerIndices: correctAnswerIndices, // Store correct answers for identifying mistakes
          userAnswerIndices: answers, // Store user answers for mistake tracking
          completedAt: serverTimestamp(),
          jobTitle: testDoc?.jobTitle || "Unknown Position",
          hasCompletedInterview: false,
          isSkillsValidation: isSkillsValidation || false, // Ensure boolean, not undefined
        });
        console.log("✅ Database save successful!");

        // Check for achievements after test completion
        try {
          console.log("🏆 Checking for unlocked achievements...");
          await checkTestAchievements(user.id, scorePercent);
          console.log("✅ Achievement check completed!");
        } catch (achievementError) {
          console.error("⚠️ Achievement check failed (non-critical):", achievementError);
        }

        // Handle skills validation test completion
        if (isSkillsValidation) {
          console.log("🎯 Processing skills validation test...");
          // Stop proctoring for skills validation tests
          stopProctoring();
          // For skills validation tests, handle verification and profile update
          await handleSkillsValidationCompletion(scorePercent, mcqScore);
          return; // Exit early, don't proceed to Round 2
        }

        // Check if candidate qualifies for Round 2 (30% of 25 = 7.5 points minimum)
        console.log("🎖️ Checking qualification for Round 2:", {
          mcqScore,
          qualifies: mcqScore >= 7.5,
        });
        if (mcqScore >= 7.5) {
          console.log(
            "🎉 Qualified for Round 2! Creating interview session..."
          );
          // Create interview session
          const sessionId = `${user.id}_${testId}_${Date.now()}`;
          const jobDescription =
            testDoc?.jobDescription ||
            testDoc?.testData?.description ||
            `Job position for ${testDoc?.jobTitle}`;

          // Generate first interview question
          const firstQuestion = await generateFirstInterviewQuestion(
            jobDescription,
            testDoc?.jobTitle
          );

          await setDoc(doc(db, "interviewSessions", sessionId), {
            sessionId,
            testId,
            candidateId: user.id,
            candidateName: user.name,
            candidateEmail: user.email,
            jobDescription,
            status: "in_progress",
            currentQuestionNumber: 1,
            conversationHistory: [
              {
                questionNumber: 1,
                question: firstQuestion,
                answer: "",
                timestamp: new Date(),
              },
            ],
            startedAt: serverTimestamp(),
          });

          // Navigate to AI Interview
          // Pass hr-shared flag if this w an HR shared link
          console.log("🔄 Navigating to AI Interview:", sessionId);
          if (isHRSharedTest && !isVerified) {
            navigate(`/job-seeker/ai-interview/${sessionId}?source=hr-shared`);
          } else {
            navigate(`/job-seeker/ai-interview/${sessionId}`);
          }
        } else {
          console.log(
            "❌ Did not qualify for Round 2. Saving final results..."
          );
          // Stop proctoring for candidates who don't qualify for Round 2
          stopProctoring();
          // Candidate didn't qualify for Round 2 - complete with MCQ score only
          // Calculate correct answer indices for each question
          const correctAnswerIndices = questions.map((q) => getCorrectIndex(q));

          await setDoc(doc(db, "jobseekerResults", `${user.id}_${testId}`), {
            jobSeekerId: user.id,
            jobSeekerName: user.name,
            jobSeekerEmail: user.email,
            testId,
            hrId: testDoc?.hrId || null, // Ensure not undefined
            score: scorePercent,
            mcqScore,
            interviewScore: 0,
            totalScore: mcqScore, // Only MCQ score
            totalQuestions: questions.length,
            correctAnswers: correctCount,
            answers,
            questions: questions, // Store full question data for course generation
            correctAnswerIndices: correctAnswerIndices, // Store correct answers for identifying mistakes
            userAnswerIndices: answers, // Store user answers for mistake tracking
            completedAt: serverTimestamp(),
            jobTitle: testDoc?.jobTitle || "Unknown Position",
            hasCompletedInterview: false,
            qualifiedForInterview: false,
          });
          console.log("✅ Final results saved successfully!");

          // Check for achievements for failed tests too
          try {
            console.log("🏆 Checking for unlocked achievements...");
            await checkTestAchievements(user.id, scorePercent);
            console.log("✅ Achievement check completed!");
          } catch (achievementError) {
            console.error("⚠️ Achievement check failed (non-critical):", achievementError);
          }

          console.log("🔄 Navigating to results page...");
          // If this w HR-shared test and user is not verified, show verification prompt
          if (isHRSharedTest && !isVerified) {
            navigate("/job-seeker/results?showVerificationPrompt=true");
          } else {
            navigate("/job-seeker/results");
          }
        }

        console.log("MCQ results saved successfully");
      } catch (error) {
        console.error("❌ Error saving test results:", error);
        // Stop proctoring on error
        stopProctoring();
        // Continue to navigate even if save fails
        if (isHRSharedTest && !isVerified) {
          navigate("/job-seeker/results?showVerificationPrompt=true");
        } else {
          navigate("/job-seeker/results");
        }
      }
    } else {
      console.error("❌ No user found during test submission!");
    }
    console.log("🏁 Test submission completed");
  }, [testDoc, answers, user, testId, db, navigate, stopProctoring]);

  // Generate first interview question
  const generateFirstInterviewQuestion = async (
    jobDescription,
    jobTitle
  ) => {
    const prompt = `You are a STRICT, PROFESSIONAL technical interviewer conducting the first question of a 10-question interview.

===== POSITION DETAILS =====
JOB TITLE: ${jobTitle}

JOB DESCRIPTION:
${jobDescription}

===== INTERVIEW CONTEXT =====
- This is Question 1 of 10 in a technical interview
- This interview determines 75% of the candidate's total score
- You need to assess technical competency, problem-solving, and job fit
- Be professional but thorough in your questioning

===== FIRST QUESTION REQUIREMENTS =====
Generate an opening question that:
1. Directly relates to the SPECIFIC job requirements mentioned above
2. Assesses the candidate's foundational knowledge for this role
3. Sets the tone for a technical, professional interview
4. Requires the candidate to demonstrate relevant experience/knowledge
5. Is appropriate  interview opener but still substantive

===== OUTPUT =====
Generate ONLY the interview question, no additional text or formatting.

QUESTION 1:`;

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateTakeTestQuestion",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            testId: testId,
            questionNumber: 0,
          }),
        }
      );

      const data = await response.json();
      return (
        data.question ||
        "Can you walk me through your experience with the key technologies and skills required for this position?"
      );
    } catch (error) {
      console.error("Error generating first question:", error);
      return "Can you walk me through your experience with the key technologies and skills required for this position?";
    }
  };

  // Handle skills validation test completion
  const handleSkillsValidationCompletion = async (
    scorePercent,
    mcqScore
  ) => {
    try {
      if (!user || !testDoc) return;

      // Determine verification status (55% threshold)
      const isVerified = scorePercent >= 55;

      // Update user profile with verification status and latest resume data
      const profileDoc = await getDoc(doc(db, "userProfiles", user.id));
      const currentProfileData = profileDoc.exists() ? profileDoc.data() : null;

      // Update profile with verification status
      await setDoc(
        doc(db, "userProfiles", user.id),
        {
          ...currentProfileData,
          skillsVerified: isVerified,
          verificationScore: scorePercent,
          verificationDate: new Date(),
          lastSkillsValidationTestId: testId,
          lastSkillsValidationScore: scorePercent,
          lastValidationUpdate: new Date(),
          skillsValidationSchedule: {
            status: "completed",
            completedAt: new Date(),
            scheduledAt: testDoc?.scheduledStartAt || null,
            option: testDoc?.scheduleOption || null,
          },
          pendingSkillsValidationTestId: null,
        },
        { merge: true }
      );

      // Save complete skills validation result
      await setDoc(doc(db, "skillsValidationResults", `${user.id}_${testId}`), {
        jobSeekerId: user.id,
        jobSeekerName: user.name,
        jobSeekerEmail: user.email,
        testId,
        score: scorePercent,
        mcqScore,
        totalQuestions: testDoc.questions?.length || 0,
        correctAnswers: Object.values(answers).length,
        answers,
        isVerified: isVerified,
        verificationThreshold: 55,
        skillProfile: testDoc.skillProfile,
        completedAt: new Date(),
        testType: "skills_validation",
        resumeBasedTest: testDoc.resumeBasedTest || false,
      });

      console.log(
        `Skills validation completed. Verified: ${isVerified}, Score: ${scorePercent}%`
      );

      // Navigate to results with verification status
      navigate("/job-seeker/results", {
        state: {
          testId,
          score: scorePercent,
          isVerified,
        },
      });
    } catch (error) {
      console.error("Error handling skills validation completion:", error);
      // Still navigate to results even if update fails
      navigate("/job-seeker/results");
    }
  };

  // Check if user h taken this test
  useEffect(() => {
    const checkTestAccess = async () => {
      if (!user || !testId) return;

      // Demo tests can always be taken (don't check for previous completions)
      if (testId === "demo") {
        setHasAlreadyTaken(false);
        setCheckingAccess(false);
        return;
      }

      try {
        // Check if user h completed this test
        const q = query(
          collection(db, "jobseekerResults"),
          where("jobSeekerId", "==", user.id),
          where("testId", "==", testId)
        );

        const querySnapshot = await getDocs(q);
        setHasAlreadyTaken(!querySnapshot.empty);
        setCheckingAccess(false);
      } catch (error) {
        console.error("Error checking test access:", error);
        setCheckingAccess(false);
      }
    };

    checkTestAccess();
  }, [user, testId, db]);

  // fetch test doc
  useEffect(() => {
    const fetchTest = async () => {
      if (!testId || hasAlreadyTaken === true) return;

      // Handle demo test locally
      if (testId === "demo") {
        const demoTestDoc = {
          id: "demo",
          jobTitle: "Practice Test - Frontend Developer",
          hrEmail: "practice@vetriaai.com",
          duration: 30, // 30 minutes
          questions: [
            {
              id: 1,
              type: "multiple-choice",
              question:
                "What is the correct way to handle side effects in React functional components?",
              options: [
                "Using componentDidMount lifecycle method",
                "Using useEffect hook",
                "Using useState hook",
                "Using class-based components only",
              ],
              correctAnswer: 1,
              difficulty: "medium",
            },
            {
              id: 2,
              type: "multiple-choice",
              question:
                "Which of the following is the correct syntax for creating a React component?",
              options: [
                "function MyComponent() { return <div>Hello</div>; }",
                "const MyComponent = () => { return <div>Hello</div>; }",
                "class MyComponent extends React.Component { render() { return <div>Hello</div>; } }",
                "All of the above",
              ],
              correctAnswer: 3,
              difficulty: "easy",
            },
            {
              id: 3,
              type: "multiple-choice",
              question: "What is the purpose of React.memo()?",
              options: [
                "To memoize expensive calculations",
                "To prevent unnecessary re-renders of functional components",
                "To manage component state",
                "To handle side effects",
              ],
              correctAnswer: 1,
              difficulty: "medium",
            },
            {
              id: 4,
              type: "code-completion",
              question:
                "Complete the following React hook to manage a counter:",
              code: `const [count, setCount] = useState(0);

const increment = () => {
  // Complete this function
  _______________
};`,
              options: [
                "setCount(count + 1)",
                "setCount(prev => prev + 1)",
                "count = count + 1",
                "Both A and B are correct",
              ],
              correctAnswer: 3,
              difficulty: "medium",
            },
            {
              id: 5,
              type: "multiple-choice",
              question: "What is the Virtual DOM in React?",
              options: [
                "A copy of the real DOM kept in memory",
                "A JavaScript representation of the real DOM",
                "A faster version of the DOM",
                "All of the above",
              ],
              correctAnswer: 3,
              difficulty: "easy",
            },
          ],
          testType: "practice",
        };

        setTestDoc(demoTestDoc);
        const durationSec = (demoTestDoc.duration || 30) * 60;
        setTimeLeft(durationSec);
        return;
      }

      // Try to fetch from hrTests collection first
      let snap = await getDoc(doc(db, "hrTests", testId));

      // If not found in hrTests, try skillsValidationTests collection
      if (!snap.exists()) {
        snap = await getDoc(doc(db, "skillsValidationTests", testId));
      }

      if (snap.exists()) {
        const data = snap.data();
        setTestDoc({ id: snap.id, ...data });
        const durationSec = (data.duration || 60) * 60;
        setTimeLeft(durationSec);
      }
    };
    fetchTest();
  }, [testId, hasAlreadyTaken, db]);

  // Show proctoring setup when test loads and not already taken (except for demo tests)
  useEffect(() => {
    if (
      testDoc &&
      hasAlreadyTaken === false &&
      !testStarted &&
      !proctoringInitialized
    ) {
      // Skip proctoring setup for demo/practice tests
      if (testDoc.testType === "practice" || testId === "demo") {
        setTestStarted(true);
        setProctoringInitialized(true);
        return;
      }
      setShowProctoringSetup(true);
    }
  }, [testDoc, hasAlreadyTaken, testStarted, proctoringInitialized, testId]);

  // Cleanup proctoring on unmount
  useEffect(() => {
    return () => {
      stopProctoring();
    };
  }, [stopProctoring]);

  // countdown
  useEffect(() => {
    if (timeLeft === null) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, handleSubmitTest]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleAnswerSelect = (selectedOptionIndex) => {
    setAnswers((previousQuestionIndexToAnswerIndex) => ({
      ...previousQuestionIndexToAnswerIndex,
      [currentQuestion]: selectedOptionIndex,
    }));
  };

  const handleNextQuestion = () => {
    const qs = getQuestions();
    if (qs.length === 0) return;
    setCurrentQuestion((prev) => Math.min(prev + 1, qs.length - 1));
  };

  const handlePreviousQuestion = () => {
    setCurrentQuestion((prev) => Math.max(prev - 1, 0));
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking test access...</p>
        </div>
      </div>
    );
  }

  if (hasAlreadyTaken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Test Already Completed
          </h2>
          <p className="text-gray-600 mb-6">
            You have already taken this test. Each test can only be taken once.
          </p>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate("/job-seeker/available-tests")}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors relative group"
            >
              Browse Tests
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Browse available tests to take
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </button>
            <button
              onClick={() => navigate("/job-seeker/results")}
              className="flex-1 px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative group"
            >
              View Results
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                View your test results
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!testDoc) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    );
  }

  const questions = getQuestions();
  const currentQ = questions[currentQuestion];
  const progress = questions.length
    ? ((currentQuestion + 1) / questions.length) * 100
    : 0;
  const answeredQuestions = Object.keys(answers).length;

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Submitting Your Test
          </h2>
          <p className="text-gray-600">
            Please wait while we process your answers...
          </p>
        </div>
      </div>
    );
  }

  // Don't render the test until proctoring is set up (unless it's a skills validation test)
  if (
    testDoc &&
    hasAlreadyTaken === false &&
    !testStarted &&
    !testStarted
  ) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProctoringSetupModal
          isOpen={showProctoringSetup}
          onClose={() => {
            setShowProctoringSetup(false);
            navigate("/job-seeker/available-tests");
          }}
          onStartProctoring={handleStartProctoring}
          requireCamera={true}
          requireMicrophone={false}
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">
              Setting up proctored test environment...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Allow demo tests, skills validation tests, and HR-shared tests without verification
  // HR-shared tests (source=hr-shared) bypass verification  HR directly invited the candidate
  const shouldRequireVerification =
    testDoc?.testType !== "practice" &&
    testId !== "demo" &&
    !testDoc?.isSkillsValidation &&
    !testDoc?.resumeBasedTest &&
    !isHRSharedTest; // HR-shared tests bypass verification

  return (
    <VerificationGuard requireVerification={shouldRequireVerification}>
      <div className="min-h-screen bg-gray-50">
        {/* Proctoring Banner */}
        <ProctoringBanner
          proctoringStatus={proctoringStatus}
          onStopProctoring={handleStopProctoring}
          showViolationDetails={true}
        />

        {/* Header */}
        <div
          className={`bg-white border-b border-gray-200 px-6 py-4 ${proctoringStatus.isActive ? "mt-16" : ""
            }`}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {testDoc.jobTitle || testDoc.testData?.title}
              </h1>
              <p className="text-sm text-gray-600">{testDoc.hrEmail}</p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-red-600" />
                <span
                  className={`font-mono text-lg ${(timeLeft ?? 0) < 300 ? "text-red-600" : "text-gray-900"
                    }`}
                >
                  {formatTime(timeLeft ?? 0)}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Question Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-6">
                <h3 className="font-semibold text-gray-900 mb-4">Questions</h3>
                <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-3 gap-2 max-w-full">
                  {questions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => !isSkillsValidation && setCurrentQuestion(index)}
                      disabled={isSkillsValidation}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${index === currentQuestion
                          ? "bg-red-800 text-white"
                          : answers[index] !== undefined
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        } ${isSkillsValidation ? 'cursor-not-allowed opacity-70' : ''}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-red-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {answeredQuestions} of {questions.length} answered
                  </div>
                </div>
              </div>
            </div>

            {/* Question Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full">
                      Question {currentQuestion + 1}
                    </span>
                    {isSkillsValidation && (
                      <span className={`text-sm px-3 py-1 rounded-full flex items-center gap-1 font-mono font-bold ${questionTimeLeft <= 10 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-blue-50 text-blue-700'
                        }`}>
                        <Clock className="w-3 h-3" />
                        {questionTimeLeft}s
                      </span>
                    )}
                    <span
                      className={`text-sm px-3 py-1 rounded-full ${currentQ?.difficulty === "easy"
                        ? "bg-green-100 text-green-800"
                        : currentQ?.difficulty === "medium"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                        }`}
                    >
                      {currentQ?.difficulty || ""}
                    </span>
                  </div>

                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {currentQ?.question || ""}
                  </h2>

                  {currentQ?.type === "code-completion" && currentQ?.code && (
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm mb-6">
                      <pre>{currentQ?.code}</pre>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {(currentQ?.options ?? []).map(
                    (option, index) => (
                      <label
                        key={index}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${answers[currentQuestion] === index
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion}`}
                          value={index}
                          checked={answers[currentQuestion] === index}
                          onChange={(event) =>
                            handleAnswerSelect(
                              Number(event.currentTarget.value)
                            )
                          }
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${answers[currentQuestion] === index
                            ? "border-red-500 bg-red-500"
                            : "border-gray-300"
                            }`}
                        >
                          {answers[currentQuestion] === index && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <span className="text-gray-900">
                          {String.fromCharCode(65 + index)}. {option}
                        </span>
                      </label>
                    )
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestion === 0}
                    className={`flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative group ${isSkillsValidation ? 'hidden' : ''}`}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      Go to previous question
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </button>

                  <div className="flex space-x-3">
                    {currentQuestion === questions.length - 1 ? (
                      <button
                        onClick={handleSubmitTest}
                        className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors relative group"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Submit Test
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                          Submit your test answers
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={handleNextQuestion}
                        className="flex items-center px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative group"
                      >
                        Next
                        <ArrowRight className="w-4 h-4 ml-2" />
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                          Go to next question
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning for unanswered questions */}
              {answeredQuestions < questions.length && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-yellow-800 font-medium">
                        Incomplete Test
                      </p>
                      <p className="text-yellow-700 text-sm mt-1">
                        You have {questions.length - answeredQuestions}{" "}
                        unanswered questions. Make sure to answer all questions
                        before submitting.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </VerificationGuard>
  );
};

export default TakeTest;
